import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, App, Button, Dropdown, Empty, Tag, Typography } from 'antd';

import { api } from '../../api/client';
import { API_COLLECTIONS_URL } from '../../utils';
import { buildActionMenu } from '../common/actionMenu';
import { confirmDestructive } from '../common/confirmDestructive';
import { buildMemberCardOptions } from '../cards/CardVisuals';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { getCollectionMemberCardIds } from '../../utils/collections';
import { copyTextToClipboard } from '../../utils/clipboard';
import { formatCount, joinMeta } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import {
	addressLabel, addressUrl, canPause, canResume, describePublicFormError, formStatus, isInviteDraft, isPublicDraft, isRetired, unavailableText,
} from '../../utils/publicForms';
import { pausePublicForm, resumePublicForm, retirePublicForm } from '../../services/publicForms';
import { labelOf as elementLabelOf } from '../../utils/intakeLinks';
import { CHART_LINE, KEBAB, PLAYER_PAUSE, PLAYER_PLAY, PLUS } from '../icons';
import PublicFormModal from './PublicFormModal';
import PublicFormQrModal from './PublicFormQrModal';
import PublicFormAddressModal from './PublicFormAddressModal';
import FormVolume from './FormVolume';
import PublicFormNotifications from './PublicFormNotifications';
import ReviewRulesModal from './ReviewRulesModal';
import { reviewSummary } from '../../utils/reviewRules';

const { Text } = Typography;

/**
 * The member cards of the SAVED collection, as the form editor offers them —
 * the Sharing tab's reading (SharingTab.js): server truth, because the
 * settings form's copy may hold unsaved Card Library edits, and a form for a
 * card the server has not saved yet is refused.
 */
function useMemberCardOptions(collectionId, collection) {
	const [saved, setSaved] = useState(null);
	useEffect(() => {
		if (!collectionId) return undefined;
		let active = true;
		api.get(`${API_COLLECTIONS_URL}/${collectionId}`)
			.then(({ data }) => { if (active) setSaved(data); })
			.catch(() => {}); // the prop below stands in
		return () => { active = false; };
	}, [collectionId]);
	const cardDefinitions = useOptionalCardDefinitions();
	const defsById = cardDefinitions?.cardsById;
	const source = saved || collection;
	const cardOptions = useMemo(() => {
		const memberIds = getCollectionMemberCardIds(source, Object.values(defsById || {}));
		const refs = Array.isArray(source?.cards) ? source.cards : [];
		const nameByRefId = new Map(refs.map(ref => [normalizeId(ref?.cardDefinitionId), ref?.name]).filter(([id]) => id));
		const fromDefs = buildMemberCardOptions(Object.values(defsById || {}), memberIds);
		const covered = new Set(fromDefs.map(opt => opt.value));
		const refOnly = [...memberIds]
			.filter(id => !covered.has(id))
			.map(id => ({ value: id, card: { name: nameByRefId.get(id) || 'Untitled card' }, depth: 0 }));
		return [...fromDefs, ...refOnly];
	}, [source, defsById]);
	return { cardOptions, cardsById: defsById || {}, getElementsForCard: cardDefinitions?.getElementsForCard || null };
}

/**
 * Collection settings → **Public forms** (KPR-513; keepr-api docs/plans/
 * permanent-public-forms.md § Web surfaces; the owner's rulings f01, f02,
 * f04, f08). The collection's PERMANENT public forms — each at a readable
 * address, `/f/<collection>/<form>`, that never expires — in their own
 * section, apart from the invite links, which stay under Sharing (f01).
 *
 * A row: the form's name, its address, its state (Live, Paused, "Paused by
 * keepr" with why — a spike, failed bot checks, abuse reports (KPR-514) —
 * "Paused — review queue full" when the backpressure holds it, Draft, Retired), what
 * it took in the last day and how many wait for review, and "Maker left"
 * when whoever made it no longer manages the collection (f08 — the form
 * stays up; the collection owns it). Pause and Resume are one click, on the
 * row (f04). Everything else is behind its ⋯, in the taxonomy's order:
 * Edit…, Review rules…, Point address here…, Show volume (Record); Copy
 * address, Show QR code… (Share & export); Retire… (Danger, confirmed); and,
 * once a rule has accepted anything, Show what rules accepted (Go to).
 *
 * REVIEW RULES (KPR-482, ReviewRulesModal): a form's accept and reject rules.
 * The row says how many it has, and — in the warning colour — when one
 * stopped working (it, and the rules after it, then decide nothing).
 *
 * Under the list, keepr's two notifications about the forms
 * (PublicFormNotifications, KPR-514), when the API lists them.
 *
 * A DRAFT reads **Not live** — a package's prepared form among them
 * (keepr-api KPR-360: it arrives with no address, and the collection's intake
 * switch as it was) — and offers **Make it live…** on its row: the address
 * window, which says in plain words what the form will expose and that the
 * address never expires before anything goes live. An invite link a package
 * prepared is not a public form: it waits under Sharing, with the intake
 * links (f01), and is not listed here.
 *
 * The tab exists only when the API has public forms and its switch is on —
 * or, with the server's switch off, when a public draft is here to be shown
 * not live (hooks/usePublicForms, read by CollectionForm); `publicForms` is
 * that reading, and every act here reloads it.
 */
export default function PublicFormsTab({ collectionId, collection, publicForms }) {
	const { message, modal } = App.useApp();
	const data = publicForms?.data || null;
	const reload = publicForms?.reload;
	// Public forms only: a prepared invite link waits under Sharing (KPR-360).
	const forms = useMemo(() => (Array.isArray(data?.forms) ? data.forms.filter(form => !isInviteDraft(form)) : []), [data]);
	const { cardOptions, cardsById, getElementsForCard } = useMemberCardOptions(collectionId, collection);
	const cardNameById = useMemo(() => new Map(cardOptions.map(option => [option.value, option.card?.name || 'Untitled card'])), [cardOptions]);
	// { form: null } to make one, { form } to edit one.
	const [editor, setEditor] = useState(null);
	const [qrFor, setQrFor] = useState(null);
	const [addressFor, setAddressFor] = useState(null);
	const [rulesFor, setRulesFor] = useState(null);
	const [busy, setBusy] = useState(null);
	const navigate = useNavigate();
	// The forms whose volume is open under their row.
	const [volumeOpen, setVolumeOpen] = useState({});

	// KPR-360: an element of a form's card, named as the card labels it.
	const labelOfFor = useCallback((form) => {
		const elements = getElementsForCard ? (getElementsForCard(normalizeId(form?.card_id)) || []) : [];
		const byName = new Map(elements.map(el => [el?.name, el]));
		return name => (byName.has(name) ? elementLabelOf(byName.get(name)) : name);
	}, [getElementsForCard]);

	const unavailable = data?.unavailable || null;
	const canCreate = data?.available === true;
	const makersLeft = forms.filter(form => form?.makerLeft === true && !isRetired(form));

	const act = useCallback(async (form, call, done, fallback) => {
		const id = normalizeId(form?._id);
		setBusy(id);
		try {
			await call(collectionId, id);
			if (done) message.success(done);
		} catch (error) {
			message.error(describePublicFormError(error, {}, fallback));
		} finally {
			setBusy(null);
			await reload?.();
		}
	}, [collectionId, message, reload]);

	const pause = form => act(form, pausePublicForm, `${form.name} is paused. Its address says “This form is paused.”`, 'The form could not be paused.');
	const resume = form => act(form, resumePublicForm, `${form.name} is taking submissions again.`, 'The form could not be resumed.');
	const retire = form => confirmDestructive(modal, {
		title: `Retire ${form.name || 'this form'}?`,
		content: form.address
			? `Its address, ${addressLabel(form.address)}, stops taking submissions and opens nothing until you point it at another form. A retired form cannot be brought back; what it sent stays in the collection.`
			: 'A retired form cannot be brought back; what it sent stays in the collection.',
		okText: 'Retire',
		onOk: () => act(form, retirePublicForm, `${form.name} is retired.`, 'The form could not be retired.'),
	});
	const copyAddress = async (form) => {
		if (await copyTextToClipboard(addressUrl(form.address))) message.success('Address copied');
		else message.error('Could not copy the address');
	};
	const toggleVolume = id => setVolumeOpen(open => ({ ...open, [id]: !open[id] }));

	const handleSaved = useCallback(async (result, { created } = {}) => {
		setEditor(null);
		await reload?.();
		// A new form opens on its address and QR code: the next thing a
		// manager does with it is put it somewhere people will see it.
		if (created && result?.address) setQrFor(result);
		else message.success(created ? 'Public form saved as a draft.' : 'Public form saved.');
	}, [reload, message]);

	const handleAddressSaved = useCallback(async (saved, { movedFrom } = {}) => {
		setAddressFor(null);
		message.success(movedFrom
			? `${addressLabel(saved?.address)} now opens ${saved?.name}. ${movedFrom.name} has no address.`
			: `${saved?.name} is at ${addressLabel(saved?.address)}.`);
		await reload?.();
	}, [reload, message]);

	const handleRulesSaved = useCallback(async (saved) => {
		setRulesFor(null);
		const { count } = reviewSummary(saved?.review);
		message.success(count ? `${saved?.name || 'The form'} decides by ${formatCount(count, 'rule')}.` : `People review every submission to ${saved?.name || 'the form'}.`);
		await reload?.();
	}, [reload, message]);

	const actionsFor = (form) => {
		const id = normalizeId(form?._id);
		const retired = isRetired(form);
		const inviteDraft = isInviteDraft(form);
		const { count } = reviewSummary(form?.review);
		return [
			{ key: 'accepted-by-rule', group: 'navigate', glyph: 'items', label: 'Show what rules accepted', hidden: !count, onClick: () => navigate(`/collections/${collectionId}/items?review=accepted-by-rule`) },
			{ key: 'edit', group: 'record', glyph: 'edit', label: 'Edit…', hidden: retired || inviteDraft, onClick: () => setEditor({ form }) },
			{ key: 'rules', group: 'record', glyph: 'filter', label: 'Review rules…', hidden: retired || inviteDraft, onClick: () => setRulesFor(form) },
			// A draft goes live from its row's own button (Make it live…).
			{ key: 'address', group: 'record', glyph: 'relate', label: form.address ? 'Point another address here…' : 'Give it an address…', hidden: retired || inviteDraft || isPublicDraft(form), onClick: () => setAddressFor(form) },
			{ key: 'volume', group: 'record', icon: <CHART_LINE />, label: volumeOpen[id] ? 'Hide volume' : 'Show volume', onClick: () => toggleVolume(id) },
			{ key: 'copy', group: 'share', glyph: 'share', label: 'Copy address', hidden: !form.address, onClick: () => copyAddress(form) },
			{ key: 'qr', group: 'share', glyph: 'print', label: 'Show QR code…', hidden: !form.address, onClick: () => setQrFor(form) },
			{ key: 'retire', group: 'destructive', glyph: 'archive', label: 'Retire…', hidden: retired, onClick: () => retire(form) },
		];
	};

	const renderRow = (form) => {
		const id = normalizeId(form?._id);
		const status = formStatus(form);
		const menu = buildActionMenu(actionsFor(form));
		const counts = form.counts || {};
		const meta = joinMeta(
			cardNameById.get(normalizeId(form.card_id)) || null,
			counts.lastDay ? `${formatCount(counts.lastDay, 'submission')} in the last day` : null,
			counts.pending ? `${formatCount(counts.pending, 'submission')} waiting for review` : null
		);
		return (
			<li key={id} className="public-forms__item" data-testid="public-form-row">
				<div className="public-forms__row">
					<div className="public-forms__main">
						<span className="public-forms__name">{form.name || 'Untitled form'}</span>
						{form.address ? (
							<span className="public-forms__address" title={addressUrl(form.address)}>{addressLabel(form.address)}</span>
						) : (
							<span className="public-forms__address public-forms__address--none">No address</span>
						)}
						{meta ? <span className="public-forms__meta">{meta}</span> : null}
						{status.hint ? <span className="public-forms__meta">{status.hint}</span> : null}
					</div>
					<div className="public-forms__tags">
						<Tag color={status.color} className="public-forms__status" data-testid="public-form-status">{status.label}</Tag>
						{reviewSummary(form.review).count ? (
							<Tag className="public-forms__rules" data-testid="public-form-rules">{formatCount(reviewSummary(form.review).count, 'rule')}</Tag>
						) : null}
						{reviewSummary(form.review).broken ? (
							<Tag color="gold" className="public-forms__rules-broken" title="A rule stopped working: it and the rules after it decide nothing." data-testid="public-form-rule-broken">A rule stopped working</Tag>
						) : null}
						{form.makerLeft && !isRetired(form) ? (
							<Tag className="public-forms__maker-left" title="Whoever made it no longer manages this collection. It keeps working.">Maker left</Tag>
						) : null}
					</div>
					<div className="public-forms__actions">
						{isPublicDraft(form) ? (
							<Button
								disabled={!canCreate}
								title={!canCreate ? unavailableText(unavailable) || undefined : undefined}
								onClick={() => setAddressFor(form)}
								aria-label={`Make ${form.name || 'this form'} live`}
								data-action="public-form-make-live"
							>
								Make it live…
							</Button>
						) : null}
						{canPause(form) ? (
							<Button
								icon={<PLAYER_PAUSE />}
								loading={busy === id}
								onClick={() => pause(form)}
								aria-label={`Pause ${form.name}`}
								data-action="public-form-pause"
							>
								Pause
							</Button>
						) : null}
						{canResume(form) ? (
							<Button
								icon={<PLAYER_PLAY />}
								loading={busy === id}
								disabled={!canCreate}
								title={!canCreate ? unavailableText(unavailable) || undefined : undefined}
								onClick={() => resume(form)}
								aria-label={`Resume ${form.name}`}
								data-action="public-form-resume"
							>
								Resume
							</Button>
						) : null}
						{menu ? (
							<Dropdown menu={menu} trigger={['click']} placement="bottomRight">
								<Button type="text" icon={<KEBAB />} aria-label={`Actions for ${form.name || 'this form'}`} />
							</Dropdown>
						) : null}
					</div>
				</div>
				{volumeOpen[id] ? (
					<FormVolume collectionId={collectionId} source={{ kind: 'form', id }} name={form.name} />
				) : null}
			</li>
		);
	};

	const live = forms.filter(form => !isRetired(form));
	const retired = forms.filter(isRetired);

	return (
		<div className="settings-column public-forms" data-testid="public-forms-tab">
			<section className="form-panel">
				<div className="form-panel__kicker-row public-forms__head">
					<div className="micro-label form-panel__kicker">Public forms</div>
					<Button
						type="primary"
						icon={<PLUS />}
						disabled={!canCreate}
						onClick={() => setEditor({ form: null })}
						data-action="new-public-form"
					>
						New public form
					</Button>
				</div>
				<Text type="secondary" className="form-panel__hint">
					A form at an address that never expires — for a sign, a poster or a web page. Anyone who reads the
					address can send this collection an item, and each one waits for review before it joins the list.
					For a form only certain people should have, make an intake link under Sharing instead.
				</Text>

				{!canCreate && unavailableText(unavailable) ? (
					<Alert
						className="public-forms__notice"
						type="info"
						showIcon
						message={unavailableText(unavailable)}
						action={unavailable === 'intake_not_enabled' ? (
							<Link to={`/collections/${collectionId}/form?tab=sharing`}>Open Sharing</Link>
						) : null}
						data-testid="public-forms-unavailable"
					/>
				) : null}

				{makersLeft.length ? (
					<Alert
						className="public-forms__notice"
						type="warning"
						showIcon
						message={makersLeft.length === 1
							? `${makersLeft[0].name} was made by someone who no longer manages this collection`
							: `${makersLeft.length} forms were made by someone who no longer manages this collection`}
						description={makersLeft.length === 1
							? 'It keeps working as it is: the collection owns its public forms, and its managers run them.'
							: 'They keep working as they are: the collection owns its public forms, and its managers run them.'}
						data-testid="public-forms-maker-left"
					/>
				) : null}

				{live.length ? (
					<ul className="public-forms__list" aria-label="Public forms">{live.map(renderRow)}</ul>
				) : (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={canCreate ? 'No public forms yet. Make one to put this collection on a sign.' : 'No public forms yet.'}
					/>
				)}

				{retired.length ? (
					<>
						<div className="micro-label public-forms__retired-label">Retired</div>
						<ul className="public-forms__list public-forms__list--retired" aria-label="Retired public forms">{retired.map(renderRow)}</ul>
					</>
				) : null}
			</section>

			<PublicFormNotifications
				collectionId={collectionId}
				notifications={data?.notifications}
				onChanged={reload}
			/>

			<PublicFormModal
				open={Boolean(editor)}
				form={editor?.form || null}
				onClose={() => setEditor(null)}
				onSaved={handleSaved}
				collectionId={collectionId}
				collectionSlug={data?.collectionSlug || null}
				suggestedCollectionSlug={data?.suggestedCollectionSlug || null}
				cardOptions={cardOptions}
				cardsById={cardsById}
				getElementsForCard={getElementsForCard}
			/>
			<PublicFormQrModal open={Boolean(qrFor)} form={qrFor} onClose={() => setQrFor(null)} />
			<ReviewRulesModal
				open={Boolean(rulesFor)}
				onClose={() => setRulesFor(null)}
				onSaved={handleRulesSaved}
				collectionId={collectionId}
				source={rulesFor ? { kind: 'form', id: normalizeId(rulesFor._id) } : null}
				name={rulesFor?.name || ''}
				definition={rulesFor?.definition || null}
				review={rulesFor?.review || null}
				cardId={rulesFor?.card_id || null}
				cardsById={cardsById}
				getElementsForCard={getElementsForCard}
				published={['published', 'retiring'].includes(collection?.publication?.state)}
				perDay={Number.isInteger(rulesFor?.limits?.perDay) ? rulesFor.limits.perDay : null}
			/>
			<PublicFormAddressModal
				open={Boolean(addressFor)}
				form={addressFor}
				cardName={addressFor ? (cardNameById.get(normalizeId(addressFor.card_id)) || null) : null}
				labelOf={addressFor ? labelOfFor(addressFor) : undefined}
				forms={forms}
				onClose={() => setAddressFor(null)}
				onSaved={handleAddressSaved}
				collectionId={collectionId}
				collectionSlug={data?.collectionSlug || null}
				suggestedCollectionSlug={data?.suggestedCollectionSlug || null}
			/>
		</div>
	);
}
